// "I finished this volume" marks — the store behind the end-of-volume Mark as
// read control and the library's read-volumes-sink-to-the-back stacking.
//
// TWO STORES, ONE READ. localStorage is always written, so an anonymous reader
// gets the entire feature (R7a's rule, and the reason the reading path never
// requires Convex); the Convex rows are written IN ADDITION when signed in, so
// the marks follow a reader between devices. Reads take the UNION, which is the
// only merge that cannot lose a mark.
//
// A UNION HAS NO TOMBSTONES, and unmarking is where that shows. Unmarking
// deletes the cloud row and this browser's local copy — but a copy sitting in
// ANOTHER browser's localStorage is not reachable from here, so a volume marked
// on a phone and unmarked on a laptop stays marked on the phone. The alternative
// is storing negative marks and merging by timestamp, which is a sync protocol,
// for a decoration on a shelf. The same shape as the gap bookmarks already carry
// (R7b): marks made anonymously in one browser do not follow a later sign-in
// elsewhere. They are not lost — that browser still shows them — and the first
// mark made while signed in starts populating the cloud copy.
//
// A mark is keyed by (bookSlug, last chapter of the volume), never by a shelf
// edition id; `convex/model/readMark.ts` has the reasoning.

import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@bindery/convex-retells/_generated/api";
import { useAuthState } from "@/lib/auth";
import { getPref, setPref } from "@/lib/prefs";
import { useLastLoaded } from "@/lib/use-last-loaded";
import type { LocalReadMark } from "@bindery/shared/corpus/reader";

/** Composite key for the union — the pair that identifies one volume. */
function markKey(bookSlug: string, chapterNumber: number): string {
  return `${bookSlug}/${String(chapterNumber)}`;
}

/** Every locally stored mark. */
export function loadReadMarks(): LocalReadMark[] {
  return getPref("readMarks", []);
}

/**
 * Set or clear one local mark, returning the full updated list so the caller
 * can render it without re-reading storage. Pure apart from the write itself:
 * the input list is never mutated.
 */
export function setLocalReadMark(
  bookSlug: string,
  chapterNumber: number,
  read: boolean,
): LocalReadMark[] {
  const key = markKey(bookSlug, chapterNumber);
  const others = loadReadMarks().filter(
    (m) => markKey(m.bookSlug, m.chapterNumber) !== key,
  );
  const next = read
    ? [...others, { bookSlug, chapterNumber, markedAt: Date.now() }]
    : others;
  setPref("readMarks", next);
  return next;
}

/** What a surface needs to render and change read state. */
export interface ReadMarks {
  /** Has the volume ending at this chapter been finished? */
  isRead: (bookSlug: string, chapterNumber: number | null) => boolean;
  /** Mark or unmark it — writes localStorage always, Convex when signed in. */
  setRead: (bookSlug: string, chapterNumber: number, read: boolean) => void;
  /**
   * Have BOTH halves of the union actually loaded? False while auth is still
   * resolving, and while a signed-in reader's cloud marks are in flight.
   *
   * A surface that just renders a mark (`volume-end.tsx`) can ignore this and
   * flip a checkbox when the answer arrives. A surface that SORTS by read state
   * cannot: it would lay out its shelf, then re-lay it out under the reader's
   * eyes. Those callers wait on `settled` before painting.
   *
   * LATCHES within a mount: once true it stays true, because the cloud half is
   * held across the query restarts an auth flip causes (see `useLastLoaded`
   * below). A gate that unmounted a painted shelf whenever `settled` dipped
   * would be the very re-layout this flag exists to prevent.
   */
  settled: boolean;
}

/**
 * The reader's read marks, local ∪ cloud.
 *
 * The local half is `useState` seeded once from storage rather than re-read on
 * every render: storage is only written through `setRead` below, so component
 * state and storage cannot drift within a mount, and a fresh mount re-reads.
 * The cloud half is skipped entirely while anonymous — `readMarks.list`
 * requires a session, so subscribing without one would only throw.
 *
 * `useAuthState` rather than `useIsAuthenticated` because `settled` has to
 * distinguish "not signed in" from "don't know yet", and the latter collapses
 * into the former under `useIsAuthenticated` — which would report the marks
 * settled before we know whether there are cloud ones to wait for.
 * `AccountControls` (`src/components/app-nav.tsx`) makes the same swap for the
 * same reason.
 */
export function useReadMarks(): ReadMarks {
  const authState = useAuthState();
  const authenticated = authState === "authenticated";
  // Held across the query restarts a transient auth flip causes: `fetchToken`
  // swallows a network blip into `null`, which dips `authState` through
  // "anonymous" and back, and each pass restarts this query as `undefined`.
  // Unheld, that would un-latch `settled` (unmounting a shelf the reader is
  // using) and shrink the key union (re-sorting it). A REAL sign-out is a full
  // navigation (`signOut`), so a held value cannot leak marks into a
  // signed-out view — and the marks held are this same reader's regardless.
  const cloud = useLastLoaded(
    useQuery(api.readMarks.list, authenticated ? {} : "skip"),
  );
  const setCloudRead = useMutation(api.readMarks.setRead);
  const [local, setLocal] = useState(loadReadMarks);

  // A dozen entries at most (one per volume the reader has finished), rebuilt
  // per render rather than memoized — cheaper than the dependency comparison
  // would be, and it keeps the two halves impossible to get out of step.
  const keys = new Set([
    ...local.map((m) => markKey(m.bookSlug, m.chapterNumber)),
    ...(cloud ?? []).map((m) => markKey(m.bookSlug, m.chapterNumber)),
  ]);

  return {
    settled: authState !== "unknown" && (!authenticated || cloud !== undefined),
    isRead: (bookSlug, chapterNumber) =>
      chapterNumber !== null && keys.has(markKey(bookSlug, chapterNumber)),
    setRead: (bookSlug, chapterNumber, read) => {
      setLocal(setLocalReadMark(bookSlug, chapterNumber, read));
      if (authenticated) {
        // Best-effort: the local write above is what the reader sees, and a
        // failed sync must not throw out of a click handler. The next mark
        // retries the pair, since this row is upserted by key rather than
        // appended.
        void setCloudRead({ bookSlug, chapterNumber, read }).catch(() => {
          /* offline or session expired — localStorage still holds the mark */
        });
      }
    },
  };
}
